import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HeadersFunction, MetaFunction, useLoaderData, useSearchParams } from "react-router";

import { Funnel, FunnelX } from "lucide-react";
import { SiYoutubeshorts } from "react-icons/si";
import InfiniteScroll from "react-infinite-scroll-component";
import DohaChopaiFilter from "~/components/chopai/DohaChopaiFilter";
import DohaChopaiFilterMobile from "~/components/chopai/DohaChopaiFilterMobile";
import ListView from "~/components/chopai/ListView";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import LoadingSpinner from "~/components/shared-components/LoadingSpinner";
import NodataFound from "~/components/shared-components/NoDataFound";
import NoHasMoreText from "~/components/shared-components/NoHasMoreText";
import { Sheet, SheetContent, SheetTrigger } from "~/components/ui/sheet";
import dohaChopaiFilterData from "~/data/dohaChopaiFilter.json";
import useDisplayStickyButton from "~/hook/useDisplayStickyButton";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import useLocale from "~/hook/useLocale";
import { useMediaQuery } from "~/hook/useMediaQuery";
import { DohaChopaiResponseType } from "~/interface/doha-chopai/doha-chopai";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { cn } from "~/lib/utils";
import { MstPersonType } from "~/type";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

export interface LoaderData {
  chopaiData: DohaChopaiResponseType;
  title: string;
  type: string | null;
  categoryData: MstPersonType[];
  chopaiSource: MstPersonType[];
}

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";

  const meta = getPageMeta("dohaChopai", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader = async ({ params, request }: { params: { lang: string }; request: Request }): Promise<any> => {
  const url = new URL(request.url);
  const pageIndex = parseInt(url.searchParams.get("pageIndex") || "1", 10);
  const type = url.searchParams.get("type") || null;
  const category = url.searchParams.get("category") || null;
  const chopai_id = url.searchParams.get("id") || null;
  const source = url.searchParams.get("source") || null;
  const { lang } = params;
  const apiService = new ApiService(lang || LANG.english);
  const masterRes = await apiService.getMasterData();
  const categoryData: MstPersonType[] = masterRes?.doha_chopai_category || [];
  const chopaiSource: MstPersonType[] = masterRes?.chopai_source || [];

  // Convert category text from URL to ID for API call
  let categoryId = null;
  if (category && categoryData.length > 0) {
    const foundCategory = categoryData.find((cat) => cat.text.toLowerCase().replace(/\s+/g, "-") === category);
    categoryId = foundCategory?.id || null;
  }

  // Convert source text from URL to ID for API call
  let sourceId = null;
  if (source && chopaiSource.length > 0) {
    const foundSource = chopaiSource.find((src) => src.text.toLowerCase().replace(/\s+/g, "-") === source);
    sourceId = foundSource?.id || null;
  }

  // Convert language-specific type to English type for server
  let serverType = null;
  if (type) {
    const foundTypeItem = dohaChopaiFilterData.find((item) => item.enName === type || item.guName === type || item.type === type);
    serverType = foundTypeItem?.type || null;
  }

  const payload = {
    pageSize: 20,
    pageIndex,
    type: serverType,
    category: categoryId,
    id: Number(chopai_id) || null,
    source: Number(sourceId) || null,
  };
  const response = await apiService.getDohaChopaiData(payload);
  const DATA = {
    chopaiData: response,
    type: type || null,
    title: lang === LANG.gujarati ? "દોહા ચોપાઈ" : "Doha Chopai",
    categoryData: categoryData || [],
    chopaiSource: chopaiSource || [],
  };

  const etag = computeEtagFromData(DATA);

  const ifNoneMatch = request.headers.get("If-None-Match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.CHOPAI.HEADERS, ETag: etag },
  });
};

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
  breadcrumbSchema: (match: any) => [
    { name: "Home", path: "/" },
    { name: match.data?.title || "Doha Chopai", path: "/chopai" },
  ],
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

const DohaChopai = () => {
  const { chopaiData, type, categoryData, chopaiSource } = useLoaderData<LoaderData>();
  const { normalInter } = useFontStyle();

  const lang = useLanguageParams();
  const { t } = useTranslation();
  const apiService = new ApiService(lang || LANG.english);
  const [searchParams, setSearchParam] = useSearchParams();
  const { LocalizedLink } = useLocale();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const { showTopButton } = useDisplayStickyButton();

  const observerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Convert initial type from URL to English type for internal state
  const getInitialFilterValue = () => {
    if (!type) return null;
    const foundTypeItem = dohaChopaiFilterData.find((item) => item.enName === type || item.guName === type || item.type === type);
    return foundTypeItem?.type || null;
  };

  const [data, setData] = useState(chopaiData?.rows || []);
  const [selectedFilter, setSelectedFilter] = useState<string | number | null>(getInitialFilterValue());
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSource, setSelectedSource] = useState<number | null>(null);
  const [pageIndex, setPageIndex] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState<{
    type: string | null;
    category: number | null;
    source: number | null;
  }>({
    type: null,
    category: null,
    source: null,
  });

  // Helper function to convert text to URL-friendly format
  const textToUrlFormat = useCallback((text: string) => {
    return text.toLowerCase().replace(/\s+/g, "-");
  }, []);

  // Helper function to find category ID from URL text
  const getCategoryIdFromUrlText = useCallback(
    (urlText: string) => {
      const foundCategory = categoryData.find((cat) => textToUrlFormat(cat.text) === urlText);
      return foundCategory?.id || null;
    },
    [categoryData, textToUrlFormat],
  );

  const fetchMoreData = async () => {
    if (isLoading || !hasMore || searchParams.get("id")) return;

    setIsLoading(true);
    const currentType = searchParams.get("type") || null;
    const currentCategory = searchParams.get("category") || null;
    const currentSource = searchParams.get("source") || null;

    // Convert category text from URL back to ID for API call
    let categoryId = null;
    if (currentCategory) {
      categoryId = getCategoryIdFromUrlText(currentCategory);
    }

    // Convert source text from URL back to ID for API call
    let sourceId = null;
    if (currentSource && chopaiSource.length > 0) {
      const foundSource = chopaiSource.find((src) => src.text.toLowerCase().replace(/\s+/g, "-") === currentSource);
      sourceId = foundSource?.id || null;
    }

    // Convert language-specific type to English type for server
    let serverType = null;
    if (currentType) {
      const foundTypeItem = dohaChopaiFilterData.find(
        (item) => item.enName === currentType || item.guName === currentType || item.type === currentType,
      );
      serverType = foundTypeItem?.type || null;
    }

    const payload = {
      pageSize: 20,
      pageIndex: pageIndex + 1,
      type: serverType,
      category: categoryId,
      source: sourceId,
    };

    try {
      const response = await apiService.getDohaChopaiData(payload);
      const newData = [...data, ...response.rows];

      setData(newData);
      setPageIndex((prev) => prev + 1);

      const isAllLoaded = newData.length >= response.total;
      setHasMore(!isAllLoaded);
    } catch (error) {
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyTypeFilter = async (value: string | number) => {
    const currentParams = new URLSearchParams(searchParams.toString());

    // Remove id parameter when applying any filter
    currentParams.delete("id");

    if (value === "") {
      currentParams.delete("type");
      setSelectedFilter(null);
    } else {
      // Receive English type value from filter component
      const englishType = value.toString();

      // Convert English type to language-specific text for URL
      const selectedItem = dohaChopaiFilterData.find((item) => item.type === englishType);
      if (selectedItem) {
        const urlParamValue = lang === LANG.gujarati ? selectedItem.guName : selectedItem.enName;
        currentParams.set("type", urlParamValue);
      }
      setSelectedFilter(englishType);
    }

    setSearchParam(currentParams);
    setPageIndex(1);
    setHasMore(true);

    // Scroll to top when filter is applied
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Reload data with new filter
    const currentCategory = currentParams.get("category");
    let categoryId = null;
    if (currentCategory) {
      categoryId = getCategoryIdFromUrlText(currentCategory);
    }

    // Convert language-specific type to English type for server
    let serverType = null;
    const typeParam = currentParams.get("type");
    if (typeParam) {
      const foundTypeItem = dohaChopaiFilterData.find((item) => item.enName === typeParam || item.guName === typeParam || item.type === typeParam);
      serverType = foundTypeItem?.type || null;
    }

    const payload = {
      pageSize: 20,
      pageIndex: 1,
      type: serverType,
      category: categoryId,
    };

    try {
      const response = await apiService.getDohaChopaiData(payload);
      setData(response.rows || []);
      const isAllLoaded = response.rows.length >= response.total;
      setHasMore(!isAllLoaded);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
      setData([]);
      setHasMore(false);
    }
  };

  const handleApplyCategoryFilter = async (categoryId: number | string) => {
    const currentParams = new URLSearchParams(searchParams.toString());

    // Remove id parameter when applying any filter
    currentParams.delete("id");

    if (categoryId === "") {
      currentParams.delete("category");
      setSelectedCategory(null);
    } else {
      const numericId = typeof categoryId === "string" ? parseInt(categoryId, 10) : categoryId;
      const selectedCategoryData = categoryData.find((cat) => cat.id === numericId);
      const categoryText = selectedCategoryData?.text;

      const isSameCategory = selectedCategory === numericId;
      const newCategory = isSameCategory ? null : numericId;

      if (newCategory && categoryText) {
        const urlFriendlyText = textToUrlFormat(categoryText);
        currentParams.set("category", urlFriendlyText);
        setSelectedCategory(newCategory);
      } else {
        currentParams.delete("category");
        setSelectedCategory(null);
      }
    }

    setSearchParam(currentParams);
    setPageIndex(1);
    setHasMore(true);

    // Scroll to top when filter is applied
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Reload data with new filter
    const currentType = currentParams.get("type");
    let categoryParam = null;
    if (currentParams.get("category")) {
      categoryParam = getCategoryIdFromUrlText(currentParams.get("category") || "");
    }

    // Convert language-specific type to English type for server
    let serverType = null;
    if (currentType) {
      const foundTypeItem = dohaChopaiFilterData.find(
        (item) => item.enName === currentType || item.guName === currentType || item.type === currentType,
      );
      serverType = foundTypeItem?.type || null;
    }

    const payload = {
      pageSize: 20,
      pageIndex: 1,
      type: serverType,
      category: categoryParam,
    };

    try {
      const response = await apiService.getDohaChopaiData(payload);
      setData(response.rows || []);
      const isAllLoaded = response.rows.length >= response.total;
      setHasMore(!isAllLoaded);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
      setData([]);
      setHasMore(false);
    }
  };

  const handleApplySourceFilter = async (sourceId: string | number) => {
    const currentParams = new URLSearchParams(searchParams.toString());
    // Remove id parameter when applying any filter
    currentParams.delete("id");

    if (sourceId === "") {
      currentParams.delete("source");
      setSelectedSource(null);
    } else {
      const numericId = typeof sourceId === "string" ? parseInt(sourceId, 10) : sourceId;
      const selectedSourceData = chopaiSource.find((src) => src.id === numericId);
      const categoryText = selectedSourceData?.text;

      const isSameSource = selectedSource === numericId;
      const newSource = isSameSource ? null : numericId;

      if (newSource && categoryText) {
        const urlFriendlyText = categoryText.toLowerCase().replace(/\s+/g, "-");
        currentParams.set("source", urlFriendlyText);
        setSelectedSource(newSource);
      } else {
        currentParams.delete("source");
        setSelectedSource(null);
      }
    }

    setSearchParam(currentParams);
    setPageIndex(1);
    setHasMore(true);

    // Scroll to top when filter is applied
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Reload data with new filter
    const currentType = currentParams.get("type");
    const currentCategory = currentParams.get("category");
    const currentSource = currentParams.get("source");
    let categoryId = null;
    if (currentCategory) {
      categoryId = getCategoryIdFromUrlText(currentCategory);
    }
    // Convert source text from URL back to ID for API call
    let sourceIdForApi = null;
    if (currentSource && chopaiSource.length > 0) {
      const foundSource = chopaiSource.find((src) => src.text.toLowerCase().replace(/\s+/g, "-") === currentSource);
      sourceIdForApi = foundSource?.id || null;
    }
    // Convert language-specific type to English type for server
    let serverType = null;
    if (currentType) {
      const foundTypeItem = dohaChopaiFilterData.find(
        (item) => item.enName === currentType || item.guName === currentType || item.type === currentType,
      );
      serverType = foundTypeItem?.type || null;
    }
    const payload = {
      pageSize: 20,
      pageIndex: 1,
      type: serverType,
      category: categoryId,
      source: sourceIdForApi,
    };
    try {
      const response = await apiService.getDohaChopaiData(payload);
      setData(response.rows || []);
      const isAllLoaded = response.rows.length >= response.total;
      setHasMore(!isAllLoaded);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
      setData([]);
      setHasMore(false);
    }
  };

  // Clear all filters

  const clearAllFilters = async () => {
    const currentParams = new URLSearchParams();
    // Clear all parameters including id when clearing filters
    setSearchParam(currentParams);
    setSelectedFilter(null);
    setSelectedCategory(null);
    setPageIndex(1);
    setHasMore(true);

    // Scroll to top when filters are cleared
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Reload data without filters
    const payload = {
      pageSize: 20,
      pageIndex: 1,
      type: null,
      category: null,
    };

    try {
      const response = await apiService.getDohaChopaiData(payload);
      setData(response.rows || []);
      const isAllLoaded = response.rows.length >= response.total;
      setHasMore(!isAllLoaded);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setHasMore(false);
    }
  };

  // Mobile filter handlers - these don't apply filters immediately
  const handleMobileTypeFilter = (value: string | number) => {
    if (value === "") {
      setTempFilters((prev) => ({ ...prev, type: null }));
    } else {
      const selectedValue = value.toString();
      setTempFilters((prev) => ({ ...prev, type: selectedValue }));
    }
  };

  const handleMobileCategoryFilter = (categoryId: number | string) => {
    if (categoryId === "") {
      setTempFilters((prev) => ({ ...prev, category: null }));
    } else {
      const numericId = typeof categoryId === "string" ? parseInt(categoryId, 10) : categoryId;
      setTempFilters((prev) => ({ ...prev, category: numericId }));
    }
  };

  const handleMobileSourceFilter = (sourceId: number | string) => {
    if (sourceId === "") {
      setTempFilters((prev) => ({ ...prev, source: null }));
    } else {
      const numericId = typeof sourceId === "string" ? parseInt(sourceId, 10) : sourceId;
      setTempFilters((prev) => ({ ...prev, source: numericId }));
    }
  };

  const handleMobileClearAllFilters = () => {
    setTempFilters({ type: null, category: null, source: null });
  };

  // Apply mobile filters when user clicks Apply button
  const applyMobileFilters = async () => {
    const currentParams = new URLSearchParams(searchParams.toString());

    // Apply type filter
    if (tempFilters.type) {
      // Convert English type to language-specific text for URL
      const selectedItem = dohaChopaiFilterData.find((item) => item.type === tempFilters.type);
      if (selectedItem) {
        const urlParamValue = lang === LANG.gujarati ? selectedItem.guName : selectedItem.enName;
        currentParams.delete("id");
        currentParams.set("type", urlParamValue);
      }
      setSelectedFilter(tempFilters.type);
    } else {
      currentParams.delete("type");
      setSelectedFilter(null);
    }

    // Apply category filter
    if (tempFilters.category) {
      const selectedCategoryData = categoryData.find((cat) => cat.id === tempFilters.category);
      if (selectedCategoryData) {
        const urlFriendlyText = textToUrlFormat(selectedCategoryData.text);
        currentParams.delete("id");
        currentParams.set("category", urlFriendlyText);
        setSelectedCategory(tempFilters.category);
      }
    } else {
      currentParams.delete("category");
      setSelectedCategory(null);
    }

    // Apply source filter
    if (tempFilters.source) {
      const selectedSource = chopaiSource.find((src) => src.id === tempFilters.source);
      if (selectedSource) {
        const urlFriendlyText = selectedSource.text.toLowerCase().replace(/\s+/g, "-");
        currentParams.delete("id");
        currentParams.set("source", urlFriendlyText);
      }
    } else {
      currentParams.delete("source");
    }

    setSearchParam(currentParams);
    setPageIndex(1);
    setHasMore(true);
    setIsSheetOpen(false);

    // Scroll to top when mobile filters are applied
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Reload data with new filters
    let categoryId = null;
    if (tempFilters.category) {
      categoryId = tempFilters.category;
    }

    // Use English type for server API call
    const serverType = tempFilters.type || null;

    // Use source ID for server API call
    const sourceId = tempFilters.source || null;

    const payload = {
      pageSize: 20,
      pageIndex: 1,
      type: serverType,
      category: categoryId,
      source: sourceId,
    };

    try {
      const response = await apiService.getDohaChopaiData(payload);
      setData(response.rows || []);
      const isAllLoaded = response.rows.length >= response.total;
      setHasMore(!isAllLoaded);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
      setData([]);
      setHasMore(false);
    }
  };

  useEffect(() => {
    setData(chopaiData?.rows || []);
    setPageIndex(1);
  }, [lang, chopaiData, searchParams]);

  useEffect(() => {
    const currentType = searchParams.get("type");
    const currentCategory = searchParams.get("category");
    const currentSource = searchParams.get("source");

    // Convert URL parameter back to English type for internal state
    let filterValue = null;
    if (currentType) {
      const foundTypeItem = dohaChopaiFilterData.find(
        (item) => item.enName === currentType || item.guName === currentType || item.type === currentType,
      );
      filterValue = foundTypeItem?.type || null;
    }

    let categoryValue = null;
    if (currentCategory) {
      categoryValue = getCategoryIdFromUrlText(currentCategory);
    }

    let sourceValue = null;
    if (currentSource && chopaiSource.length > 0) {
      const foundSource = chopaiSource.find((src) => src.text.toLowerCase().replace(/\s+/g, "-") === currentSource);
      sourceValue = foundSource?.id || null;
    }

    setSelectedFilter(filterValue);
    setSelectedCategory(categoryValue);
    setSelectedSource(sourceValue);

    // Sync temp filters with current URL params
    setTempFilters({
      type: filterValue,
      category: categoryValue,
      source: sourceValue,
    });
  }, [searchParams, categoryData, chopaiSource, getCategoryIdFromUrlText]);

  return (
    <div className={cn("px-3 md:px-0 bg-primary mb-5", data.length > 10 ? "min-h-screen" : "min-h-auto")}>
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-[20%_60%] gap-6 justify-center ">
        {/* Desktop Filter Sidebar */}

        <div className="hidden md:block sticky top-20 max-h-[calc(100vh-5rem)]">
          <div className="mb-4">
            <LocalizedLink
              to="/chopai-view"
              query={{
                type: searchParams.get("type") ?? undefined,
                category: searchParams.get("category") ?? undefined,
                source: searchParams.get("source") ?? undefined,
              }}
              className={cn(
                "block w-full py-2 px-4 bg-textHeadingColor body_text_m text-white rounded-2xl hover:bg-opacity-90 transition-all text-center",
                normalInter,
              )}
            >
              {t("chopai.scroll_view")}
            </LocalizedLink>
          </div>
          <DohaChopaiFilter
            onApplyTypeFilter={handleApplyTypeFilter}
            onApplyCategoryFilter={handleApplyCategoryFilter}
            onClearAllFilters={clearAllFilters}
            categoryData={categoryData}
            isMobile={false}
            chopaiSource={chopaiSource}
            onApplySourceFilter={handleApplySourceFilter}
          />
        </div>

        <div className="flex-1 md:ml-0">
          <InfiniteScroll
            key={`${searchParams.get("type") || "no-type"}-${searchParams.get("category") || "no-category"}`}
            dataLength={data.length}
            next={fetchMoreData}
            hasMore={hasMore}
            scrollThreshold={isMobile ? 0.5 : 0.8}
            scrollableTarget={undefined}
            loader={
              isLoading && (
                <div className="w-full flex justify-center items-center py-8">
                  <LoadingSpinner />
                </div>
              )
            }
            endMessage={
              chopaiData?.rows?.length > 0 && (
                <NoHasMoreText className="text-textSecondaryColor" text={t("common.seeAllData", { data: `Doha Chopai` })} />
              )
            }
            className="scrollbar-hide"
          >
            {chopaiData?.rows?.length > 0 ? (
              <>
                {searchParams.get("id") && (
                  <div
                    className="w-full flex justify-end items-center text-textHeadingColor px-2 cursor-pointer"
                    onClick={() => {
                      const params = new URLSearchParams(searchParams);
                      params.delete("id");
                      setSearchParam(params);
                    }}
                  >
                    Show all
                  </div>
                )}
                <div className="bg-secondaryBackground rounded-2xl">
                  <ListView data={data} ref={observerRef} category={categoryData} chopaiSource={chopaiSource} />
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center w-full h-96">
                <NodataFound text="Jay Swaminarayan 🙏 No Chopai found." />
              </div>
            )}
          </InfiniteScroll>
        </div>
      </div>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <div
          className={cn(
            "flex flex-col items-end gap-2 sticky pr-3 z-10 md:hidden transition-all duration-300 ease-out",
            showTopButton ? "bottom-28" : "bottom-20",
          )}
        >
          <SheetTrigger asChild>
            <button className="flex items-center justify-center size-11 border border-borderPrimary rounded-full bg-textHeadingColor shadow-lg hover:shadow-xl transition-shadow">
              {selectedFilter ? <FunnelX size={18} fill="white" className="text-white" /> : <Funnel size={18} fill="white" className="text-white" />}
            </button>
          </SheetTrigger>
          <LocalizedLink
            to="/chopai-view"
            query={{
              type: searchParams.get("type") ?? undefined,
              category: searchParams.get("category") ?? undefined,
              source: searchParams.get("source") ?? undefined,
            }}
            className="flex items-center justify-center size-11 border border-borderPrimary rounded-full bg-textHeadingColor shadow-lg hover:shadow-xl transition-shadow"
          >
            <SiYoutubeshorts size={20} color="white" />
          </LocalizedLink>
        </div>

        <SheetContent
          side="bottom"
          className="!p-0 md:!pb-6 w-full max-md:h-[90vh] md:max-h-[calc(100vh-150px)] overflow-hidden flex flex-col !rounded-t-2xl"
          closeIcon={false}
        >
          <DohaChopaiFilterMobile
            categoryData={categoryData}
            chopaiSource={chopaiSource}
            selectedType={selectedFilter as string | null}
            selectedCategoryId={selectedCategory}
            selectedSourceId={selectedSource}
            setIsSheetOpen={setIsSheetOpen}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default DohaChopai;
